import { DropdownSubtle } from '@designsforhealth/dfh-react-components'
import * as R from 'ramda'
import React, { FC, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'

import useSiteMetadata from '../../../hooks/useSiteMetadata'
import {
  ProductSortOption,
  sortOptionsDefault,
  sortOptionsWithLabelStatus,
  UseProducts,
} from '../../../lib/products'
import {
  isOrderByEqual,
  isOrderByEqualTo,
  ResultsListingOrdering,
} from '../../../lib/results-listing'

interface SortProductsDropdownProps {
  className?: string
  listing: UseProducts['listing']
}

const StyledDropdown = styled(DropdownSubtle)<{ $controlWidth: number }>(
  ({ $controlWidth, theme }) => ({
    alignItems: 'center',
    backgroundColor: theme.colors.grayscale.white,
    textTransform: 'unset',
    display: 'flex',
    flex: '1 50%',
    height: '60px',
    justifyContent: 'flex-end',
    marginBottom: '0',
    minWidth: 'fit-content',
    paddingRight: '15px',
    '.react-select__control': {
      marginLeft: '15px',
      width: $controlWidth,
    },
    '.react-select__dropdown-indicator': {
      paddingLeft: '0',
    },
    '.react-select__menu': {
      maxWidth: $controlWidth + 25,
      textAlign: 'right',
    },
    '.react-select__single-value': {
      marginLeft: '0',
    },
    '.react-select__value-container': {
      // adjust vertical alignment
      transform: 'translateY(-0.5px)',
    },
    '@media(min-width: 992px)': {
      backgroundColor: 'transparent',
      flex: '0 0 270px',
      paddingRight: '80px',
    },
  }),
)

const isSortOptionOrderByEqualTo: (
  a: ReadonlyArray<ResultsListingOrdering<string>>,
) => (b: ProductSortOption) => boolean = (a) =>
  R.pipe(({ orderBy }) => orderBy, isOrderByEqualTo(a))

export const SortProductsDropdown: FC<SortProductsDropdownProps> = ({ className, listing }) => {
  const { featureFlags } = useSiteMetadata() ?? {}
  const sortOptions = featureFlags?.productLabelStatus
    ? sortOptionsWithLabelStatus
    : sortOptionsDefault
  const { state, apply, nextState } = listing
  const { control, watch } = useForm<{ sort?: ProductSortOption }>()

  const sortOptionMatchingState = sortOptions.find(isSortOptionOrderByEqualTo(state.orderBy))
  const sortValue = watch('sort', sortOptionMatchingState)

  useEffect(() => {
    const sortValueOrderBy = sortValue?.orderBy ?? []
    if (!isOrderByEqual(sortValueOrderBy, state.orderBy)) {
      const next = nextState.withSortOrder(sortValueOrderBy)
      apply(next)
    }
  }, [apply, nextState, sortValue, state.orderBy])

  return (
    <StyledDropdown
      className={className}
      control={control}
      $controlWidth={sortOptions === sortOptionsWithLabelStatus ? 170 : 125}
      defaultValue={sortOptionMatchingState ?? sortOptions[0]}
      name="sort"
      options={sortOptions}
      prompt="Sort By"
    />
  )
}

SortProductsDropdown.defaultProps = {
  className: '',
}

const StyledSortProductsDropdown = styled(SortProductsDropdown)(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.colors.grayscale.white,
  display: 'flex',
  flex: '0 0 50%',
  height: '60px',
  justifyContent: 'flex-end',
  marginBottom: '0',
  paddingRight: '15px',
  '.react-select__control': {
    marginLeft: 'auto',
    width: '125px',
  },
  '.react-select__dropdown-indicator': {
    paddingLeft: '0',
  },
  '.react-select__menu': {
    maxWidth: '100px',
    textAlign: 'right',
  },
  '.react-select__single-value': {
    marginLeft: '0',
  },
  '@media(min-width: 992px)': {
    backgroundColor: 'transparent',
    flex: '0 0 270px',
    paddingRight: '80px',
  },
}))

export default StyledSortProductsDropdown
